import type { LastModifiedBy } from '@/shared/types/common';

import type { EntityFilters } from '@/shared/types/entity';

/**
 * Filters for product search/list
 */
export type ProductSearchFilters = EntityFilters & {
  status: '' | 'active' | 'inactive';
  category: string;
  allowedSubscriptions: '' | 'none' | 'cat' | 'other';
  hasSwapConfigured: '' | 'configured' | 'none';
  isOneOff: '' | 'yes' | 'no';
  isLowOnStock: '' | 'yes' | 'no';
  includeDeleted: '' | 'yes';
};

/**
 * Filter options from backend
 */
export type ProductFilterOptions = {
  categories: string[];
  subcategories: Record<string, string[]>;
  allowedSubscriptions: string[];
};

/**
 * Available swap product option from backend
 */
export type AvailableSwapProduct = {
  id: number;
  productVariantId: number;
  title: string;
  category: string | null;
  subCategory: string | null;
  multipackType: string | null;
  quantityDescription: string | null;
};

/**
 * Product badge type (from GET response)
 */
export type ProductBadge = {
  id: number;
  content: string;
  displayLocation: string;
  style: string;
};

/**
 * Product badge form data for sync (PUT request)
 */
export type ProductBadgeFormData = {
  content: string;
  displayLocation: string;
  style: string;
};

/**
 * Badge options from GET /admin/badges/options
 */
export type BadgeOptions = {
  locations: string[];
  styles: string[];
};

/**
 * Product type as received from backend API
 */
export type ProductApiResponse = {
  id: number;
  shopifyId: number;
  productVariantId: number;
  sku: string | null;
  isActive: boolean;
  title: string;
  description: string | null;
  shortDescription: string | null;
  upsellCarouselDescription: string | null;
  imageURL: string | null;
  aboutThisRecipe?: string | null;
  price: number | null;
  listPrice: number | null;
  category: string | null;
  subCategory: string | null;
  multipackType: string | null;
  quantityDescription: string | null;
  composition: string | null;
  analyticalConstituents: string | null;
  nutritionalAdditives: string | null;
  isLowOnStock: boolean;
  isOneOff: boolean;
  sortOrder: number | null;
  images?: ProductImageApiResponse[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  lastModifiedBy?: LastModifiedBy;
  isUsedInSubscription?: boolean;
  isVirtual: boolean;
  allowedSubscriptions: 'None' | 'Cat' | 'Other';
  swapWithVariantId?: number | null;
  swapStatus?: 'Active' | 'None';
  shopifyProductUrl?: string;
  groupedProduct?: ProductGrouping | null;
  varietyPackLines?: Record<string, number> | null;
  tags: string[];
};

/**
 * Product type used in frontend
 */
export type Product = {
  id: number;
  shopifyProductId: string | null;
  shopifyVariantId: string | null;
  title: string;
  primaryImageUrl: string | null;
  sku: string | null;
  actualPrice: number | null;
  listPrice: number | null;
  isVirtual: boolean;
  isActive: boolean;
  category: string | null;
  subcategory: string | null;
  aboutThisRecipe: string | null;
  packConfiguration: string | null;
  quantityDescription: string | null;
  lowStock: boolean;
  swapWithVariantId: number | null;
  allowedSubscriptions: 'None' | 'Cat' | 'Other';
  sortOrder: number | null;
  oneOff: boolean;
  description: string | null;
  shortDescription: string | null;
  upsellCarouselDescription: string | null;
  composition: string | null;
  analyticalConstituents: string | null;
  nutritionalAdditives: string | null;
  images: ProductImage[];
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
  lastModifiedBy?: LastModifiedBy;
  isUsedInSubscription?: boolean;
  summary: string;
  categoryDisplay: string;
  swapStatus: 'Active' | 'None';
  shopifyProductUrl: string | null;
  groupedProduct: ProductGrouping | null;
  varietyPackLines?: Record<string, number>;
  tags: string[];
};

export type ProductFormData = {
  shopifyProductId: string | null;
  shopifyVariantId: string | null;
  title: string;
  primaryImageUrl: string | null;
  sku: string | null;
  actualPrice: number | null;
  listPrice: number | null;
  isVirtual: boolean;
  isActive: boolean;
  category: string | null;
  subcategory: string | null;
  aboutThisRecipe: string | null;
  packConfiguration: string | null;
  quantityDescription: string | null;
  lowStock: boolean;
  swapWithVariantId: number | null;
  allowedSubscriptions: 'None' | 'Cat' | 'Other';
  sortOrder: number | null;
  oneOff: boolean;
  description: string | null;
  shortDescription: string | null;
  upsellCarouselDescription: string | null;
  composition: string | null;
  analyticalConstituents: string | null;
  nutritionalAdditives: string | null;
  images: ProductImage[];
  groupedProductId: number | null;
  varietyPackLines?: Record<string, number>;
  tags: string[];
};

export type ProductSettings = {
  categories: string[];
  subcategories: Record<string, string[]>; // category -> subcategories
  packConfigurations: string[];
};

export const PRODUCT_IMAGE_ROLES = {
  CustomerDashboardCover: 'customer-dashboard-cover',
  CustomerDashboardHover: 'customer-dashboard-hover',
  CustomerDashboardCarousel: 'customer-dashboard-carousel',
  WebsiteOurFoodCover: 'website-our-food-cover',
  WebsiteOurFoodHover: 'website-our-food-hover',
  WebsiteOurFoodCarousel: 'website-our-food-carousel',
  WebsiteOurFoodMobileCover: 'website-our-food-mobile-cover',
  WebsiteOurFoodMobileCarousel: 'website-our-food-mobile-carousel',
} as const;

export type ProductImageRole = (typeof PRODUCT_IMAGE_ROLES)[keyof typeof PRODUCT_IMAGE_ROLES];
export type ProductImageRoleSortOrders = Partial<Record<ProductImageRole, number>>;
export type ProductImageSurface = 'customerDashboard' | 'websiteOurFood';

export const PRODUCT_IMAGE_SURFACE_ROLES: Record<
  ProductImageSurface,
  {
    cover: ProductImageRole;
    hover: ProductImageRole;
    carousel: ProductImageRole;
    mobile?: { cover: ProductImageRole; carousel: ProductImageRole };
  }
> = {
  customerDashboard: {
    cover: PRODUCT_IMAGE_ROLES.CustomerDashboardCover,
    hover: PRODUCT_IMAGE_ROLES.CustomerDashboardHover,
    carousel: PRODUCT_IMAGE_ROLES.CustomerDashboardCarousel,
  },
  websiteOurFood: {
    cover: PRODUCT_IMAGE_ROLES.WebsiteOurFoodCover,
    hover: PRODUCT_IMAGE_ROLES.WebsiteOurFoodHover,
    carousel: PRODUCT_IMAGE_ROLES.WebsiteOurFoodCarousel,
    mobile: {
      cover: PRODUCT_IMAGE_ROLES.WebsiteOurFoodMobileCover,
      carousel: PRODUCT_IMAGE_ROLES.WebsiteOurFoodMobileCarousel,
    },
  },
};

export type ProductImage = {
  id?: number | null;
  url: string;
  sortOrder: number;
  roles: ProductImageRole[];
  roleSortOrders: ProductImageRoleSortOrders;
};

export type ProductImageApiResponse = {
  id?: number | null;
  imageUrl: string;
  url?: string;
  sortOrder: number;
  roles: ProductImageRole[];
  roleSortOrders: ProductImageRoleSortOrders;
};

export type ProductValidationErrors = {
  field: string;
  message: string;
}[];

export type SanityCheckWarning = {
  id: string;
  message: string;
};

/**
 * Response from Shopify prefill endpoint (ShopifyProductData)
 */
export type ShopifyPrefillResponse = {
  shopifyId: number;
  productVariantId: number;
  title: string;
  sku: string | null;
  imageURL: string | null;
  price: number;
  images?: ProductImageApiResponse[];
};

/**
 * Response from available swap products endpoint
 */
export type AvailableSwapProductsResponse = {
  availableSwapProducts: AvailableSwapProduct[];
};

/**
 * Available product for grouping
 */
export type AvailableGroupProduct = {
  id: number;
  title: string;
  productVariantId: number;
  imageUrl: string | null;
  category: string | null;
  subCategory: string | null;
  multipackType: string | null;
  quantityDescription: string | null;
};

/**
 * Response from available group products endpoint
 */
export type AvailableGroupProductsResponse = {
  groupedProductId: number | null;
  availableProducts: AvailableGroupProduct[];
};

/**
 * Grouped product reference as returned by the product API response
 */
export type ProductGrouping = {
  id: number;
  title: string;
  productVariantId: number;
};
